// Tiny module-level registry for the locally-authenticated user's
// `accountId`. The web `messagesAdapter` reads this in
// `liveQuery` to set `Message.isSelf` correctly — without it,
// every bubble renders left-aligned because the adapter has no way
// to know which `senderAccountId` belongs to the current device.
//
// Set from `apps/web` post-bootstrap (after `storeIdentity` runs)
// so that the SDK's MLS storage and the chat-list adapter share
// the same notion of "me".

let currentUserAccountId: string | null = null;

// Listeners notified whenever the registered accountId changes. The
// message adapters subscribe so their `liveQuery` projection re-emits
// (recomputing `Message.isSelf`) the moment identity resolves — without
// this, messages that rendered BEFORE bootstrap registered the account
// (a reload race) stay `isSelf=false` forever (all bubbles left-aligned
// + the wrong colour) until an unrelated Dexie write happens to re-run
// the query.
const listeners = new Set<(accountId: string | null) => void>();

export function registerCurrentUserAccountId(accountId: string | null): void {
  const next = accountId && accountId.length > 0 ? accountId : null;
  if (next === currentUserAccountId) return;
  currentUserAccountId = next;
  for (const l of listeners) {
    try {
      l(next);
    } catch {
      // a faulty listener must not break registration
    }
  }
}

export function getCurrentUserAccountId(): string | null {
  return currentUserAccountId;
}

// The locally-authenticated user's MLS `user_id` (lowercase hex of the 32-byte
// account identity from `client.userId()`). With shared-identity linking EVERY
// device of an account carries this SAME id, so an incoming SDK message whose
// `sender_user_id` equals it was sent by one of OUR OWN devices — the
// bulletproof, broadcast-independent self-attribution signal. Registered at
// bootstrap once the SDK client exists. Empty/null until then (the syncEngine
// then falls back to the device→account map).
let currentUserMlsUserIdHex: string | null = null;

export function registerCurrentUserMlsUserId(userIdHex: string | null): void {
  const next = userIdHex && userIdHex.length > 0 ? userIdHex.toLowerCase() : null;
  if (next === currentUserMlsUserIdHex) return;
  currentUserMlsUserIdHex = next;
  // Re-run message projections so bubbles flip to self the moment the MLS
  // user id resolves (same pattern as the device-set registry).
  for (const l of listeners) {
    try {
      l(currentUserAccountId);
    } catch {
      // a faulty listener must not break registration
    }
  }
}

export function getCurrentUserMlsUserId(): string | null {
  return currentUserMlsUserIdHex;
}

// The locally-authenticated user's display name. Used to detect a self
// @mention in a 1:1 chat, where the mention's `user_id` is the (per-side)
// conversation id rather than an account id, so the only reliable signal that
// YOU were tagged is the mention's `display_name` matching your own name.
let currentUserDisplayName: string | null = null;

export function registerCurrentUserDisplayName(displayName: string | null): void {
  currentUserDisplayName =
    displayName && displayName.trim().length > 0 ? displayName.trim() : null;
}

export function getCurrentUserDisplayName(): string | null {
  return currentUserDisplayName;
}

/** Subscribe to current-user changes. Returns an unsubscribe fn. */
export function subscribeCurrentUserAccountId(
  listener: (accountId: string | null) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// HEX device ids of EVERY device on the local account (this device + linked
// siblings), fetched from `GET /v1/devices/{accountId}`. Drives self-attribution
// for messages sent by a SIBLING device: the sync engine sets a received
// message's `senderAccountId` to the sender's device hex, so a message whose
// device is in this set is OUR own and must render right-aligned (`isSelf`) on
// every device — independent of any per-conversation `ping.profile` hydration.
let selfDeviceIdsHex: ReadonlySet<string> = new Set();

export function registerSelfDeviceIdsHex(ids: readonly string[]): void {
  const next = new Set(ids.map((id) => id.toLowerCase()).filter((id) => id.length > 0));
  // Skip + don't notify when unchanged (avoids redundant liveQuery re-runs).
  if (next.size === selfDeviceIdsHex.size && [...next].every((id) => selfDeviceIdsHex.has(id))) {
    return;
  }
  selfDeviceIdsHex = next;
  // Reuse the current-user listeners so message projections re-run and flip a
  // sibling's bubbles to self the moment the device set resolves.
  for (const l of listeners) {
    try {
      l(currentUserAccountId);
    } catch {
      // a faulty listener must not break registration
    }
  }
}

/** True when `deviceHex` is one of the local account's own devices. */
export function isSelfDeviceIdHex(deviceHex: string): boolean {
  return deviceHex.length > 0 && selfDeviceIdsHex.has(deviceHex.toLowerCase());
}

/** True when `userIdHex` is THIS account's shared MLS UserId (any own device).
 *  Reads the single `currentUserMlsUserIdHex` registered via
 *  `registerCurrentUserMlsUserId` at bootstrap. After the shared-identity seed
 *  transfer at link time EVERY device of the account carries this SAME UserId in
 *  its leaf credential, so a received message whose `sender_user_id` equals it is
 *  OURS — the bulletproof, fetch-free self-attribution signal (matches iOS
 *  `isSelfSender`). Independent of the `/v1/devices` device-set (which can lag a
 *  fresh link). */
export function isSelfMlsUserIdHex(userIdHex: string): boolean {
  return (
    currentUserMlsUserIdHex !== null &&
    userIdHex.length > 0 &&
    userIdHex.toLowerCase() === currentUserMlsUserIdHex
  );
}
